'use client'

import * as React from 'react'
import { ChevronsUpDown } from 'lucide-react'
import * as RPNInput from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'

import cn from '@config/tailwind/cn'

import { Button } from './button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from './combobox'
import { Input } from './input'

type PhoneInputProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value' | 'ref'> &
  Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
    onChange?: (value: RPNInput.Value) => void
    inputComponentClassName?: string
    flagComponentClassName?: string
    countrySelectComponentClassName?: string
  }

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(
  (
    {
      className,
      onChange,
      value,
      inputComponentClassName,
      flagComponentClassName,
      countrySelectComponentClassName,
      ...props
    },
    ref,
  ) => {
    // Use refs to store classNames so component references stay stable
    const inputClassNameRef = React.useRef(inputComponentClassName)
    const flagClassNameRef = React.useRef(flagComponentClassName)
    const countrySelectClassNameRef = React.useRef(countrySelectComponentClassName)

    // Keep refs in sync with props
    React.useEffect(() => {
      inputClassNameRef.current = inputComponentClassName
      flagClassNameRef.current = flagComponentClassName
      countrySelectClassNameRef.current = countrySelectComponentClassName
    })

    // Create stable component references that read from refs
    const StableInputComponent = React.useMemo(
      () =>
        React.forwardRef<HTMLInputElement, Omit<React.ComponentProps<'input'>, 'size'>>(
          function StableInput(props, inputRef) {
            return (
              <Input
                {...props}
                className={cn('ui:rounded-s-none ui:rounded-e-lg ui:h-12', props.className, inputClassNameRef.current)}
                ref={inputRef}
              />
            )
          },
        ),
      [],
    )

    const StableFlagComponent = React.useMemo(
      () =>
        function StableFlag(props: RPNInput.FlagProps) {
          return <FlagComponent {...props} className={flagClassNameRef.current} />
        },
      [],
    )

    const StableCountrySelectComponent = React.useMemo(
      () =>
        function StableCountrySelect(props: CountrySelectProps) {
          return <CountrySelect {...props} className={countrySelectClassNameRef.current} />
        },
      [],
    )

    return (
      <RPNInput.default
        ref={ref}
        className={cn('ui:flex', className)}
        flagComponent={StableFlagComponent}
        countrySelectComponent={StableCountrySelectComponent}
        inputComponent={StableInputComponent}
        smartCaret={false}
        value={value ?? undefined}
        /**
         * Handles the onChange event.
         *
         * react-phone-number-input might trigger the onChange event as undefined
         * when a valid phone number is not entered. To prevent this,
         * the value is coerced to an empty string.
         *
         * @param {E164Number | undefined} value - The entered value
         */
        onChange={(value) => onChange?.(value ?? ('' as RPNInput.Value))}
        {...props}
      />
    )
  },
)
PhoneInput.displayName = 'PhoneInput'

interface CountryEntry {
  label: string
  value: RPNInput.Country | undefined
}

interface CountrySelectProps {
  disabled?: boolean
  value: RPNInput.Country
  options: CountryEntry[]
  onChange: (country: RPNInput.Country) => void
  className?: string
}

interface CountryItem {
  label: string
  value: RPNInput.Country
}

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
  className,
}: CountrySelectProps) => {
  // Filter out entries without a country code and ensure proper typing
  const validCountries = React.useMemo<CountryItem[]>(
    () =>
      countryList
        .filter((entry): entry is CountryEntry & { value: RPNInput.Country } => !!entry.value)
        .map((entry) => ({ label: entry.label, value: entry.value })),
    [countryList],
  )

  // Find the selected country entry
  const selectedEntry = React.useMemo(
    () => validCountries.find((entry) => entry.value === selectedCountry) ?? null,
    [validCountries, selectedCountry],
  )

  return (
    <Combobox<CountryItem>
      value={selectedEntry}
      onValueChange={(entry) => {
        if (entry?.value) {
          onChange(entry.value)
        }
      }}
      items={validCountries}
      disabled={disabled}
      isItemEqualToValue={(item, selected) => item.value === selected.value}
    >
      <ComboboxTrigger
        render={
          <Button
            type='button'
            variant='outline'
            className={cn(
              'ui:flex ui:gap-1 ui:rounded-s-lg ui:rounded-e-none ui:border-r-0 ui:px-3 ui:focus:z-10 ui:h-12',
              className,
            )}
            disabled={disabled}
            size='sm'
          >
            <FlagComponent country={selectedCountry} countryName={selectedCountry} />
            <ChevronsUpDown
              className={cn('ui:-mr-2 ui:size-4 ui:opacity-50', disabled ? 'ui:hidden' : 'ui:opacity-100')}
            />
          </Button>
        }
      />
      <ComboboxContent className='ui:w-[300px]'>
        <ComboboxInput placeholder='Search country...' showTrigger={false} />
        <ComboboxEmpty>No country found.</ComboboxEmpty>
        <ComboboxList>
          {(entry: CountryItem) => (
            <ComboboxItem key={entry.value} value={entry}>
              <FlagComponent country={entry.value} countryName={entry.label} />
              <span className='ui:flex-1 ui:text-sm'>{entry.label}</span>
              <span className='ui:text-neutral-black/50 ui:text-sm ui:mr-4'>{`+${RPNInput.getCountryCallingCode(entry.value)}`}</span>
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

const FlagComponent = ({ country, countryName, className }: RPNInput.FlagProps & { className?: string }) => {
  const Flag = flags[country]

  return (
    <span
      className={cn(
        "ui:bg-neutral-white/20 ui:flex ui:h-4 ui:w-6 ui:overflow-hidden ui:rounded-sm ui:[&_svg:not([class*='size-'])]:size-full",
        className,
      )}
    >
      {Flag && <Flag title={countryName} />}
    </span>
  )
}

const formatPhoneNumber = RPNInput.formatPhoneNumber
const formatPhoneNumberIntl = RPNInput.formatPhoneNumberIntl
const getCountryCallingCode = RPNInput.getCountryCallingCode

export { PhoneInput, formatPhoneNumber, formatPhoneNumberIntl, getCountryCallingCode }
